import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  Grid,
  Chip,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  DeleteOutline,
  EventNote,
  AccessTime,
  Group,
  Link as LinkIcon,
  InfoOutlined,
} from "@mui/icons-material";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import {
  getCache,
  setCache,
  invalidateCache,
  notifyAssignmentsUpdated,
  type ComponentManager,
} from "./AssignmentsCache";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

interface Component {
  id: number;
  name: string;
  description?: string;
}

interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
}

interface FormData {
  description: string;
  version: string;
  lead_developer_id: string;
  backup_developer_id: string;
  user_lead_id: string; // NEW – person in charge of feedback
  whatsapp_group_url: string;
  figma_url?: string;
  next_audit: string; // NEW – ISO date string YYYY-MM-DD
}

const emptyForm: FormData = {
  description: "",
  version: "",
  lead_developer_id: "",
  backup_developer_id: "",
  user_lead_id: "",
  whatsapp_group_url: "",
  figma_url: "",
  next_audit: "",
};

const calcDaysToReview = (auditDateStr: string): number | null => {
  if (!auditDateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const audit = new Date(auditDateStr);
  if (isNaN(audit.getTime())) return null;
  audit.setHours(0, 0, 0, 0);
  return Math.ceil((audit.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const daysChipProps = (
  days: number | null,
): {
  label: string;
  color: "default" | "error" | "warning" | "info" | "success";
} => {
  if (days === null) return { label: "No audit date set", color: "default" };
  if (days < 0)
    return {
      label: `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`,
      color: "error",
    };
  if (days === 0) return { label: "Due today", color: "error" };
  if (days <= 7)
    return {
      label: `${days} day${days === 1 ? "" : "s"} to review`,
      color: "warning",
    };
  if (days <= 30) return { label: `${days} days to review`, color: "info" };
  return { label: `${days} days to review`, color: "success" };
};

// ─── Section header ──────────────────────────────────────────────────────────

const SectionHeader = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3, mb: 1.5 }}>
    <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, letterSpacing: 0.5, color: "text.secondary" }}
    >
      {label}
    </Typography>
  </Box>
);

const UpdateFooter = () => {
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission(PERMISSIONS.CONFIGURATION_ADD);
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const canDelete = hasPermission(PERMISSIONS.CONFIGURATION_DELETE);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checkingAssignment, setCheckingAssignment] = useState(false);

  const [serverDaysToReview, setServerDaysToReview] = useState<number | null>(
    null,
  );

  const [existingAssignment, setExistingAssignment] =
    useState<ComponentManager | null>(null);

  const [components, setComponents] = useState<Component[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  const [users, setUsers] = useState<User[]>([]);
  const [devUsers, setDevUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [openAddComponentDialog, setOpenAddComponentDialog] = useState(false);
  const [newComponentData, setNewComponentData] = useState({
    name: "",
    description: "",
  });
  const [savingNewComponent, setSavingNewComponent] = useState(false);

  const location = useLocation();

  useEffect(() => {
    fetchComponents();
    fetchUsers();
    fetchDevUsers();
  }, []);

  useEffect(() => {
    const state = location.state as {
      componentId?: number;
      componentName?: string;
    } | null;
    if (state?.componentId && components.length > 0) {
      setSelectedComponent(state.componentId.toString());
      window.history.replaceState({}, "");
    }
  }, [location.state, components]);

  const fetchAllAssignments = async (): Promise<ComponentManager[]> => {
    const cached = getCache();
    if (cached) return cached;

    let all: ComponentManager[] = [];
    let url: string | null = "/api/configurations/components-managers/";

    while (url) {
      const response: {
        data:
          | { results?: ComponentManager[]; next?: string | null }
          | ComponentManager[];
      } = await apiService.get(url);

      if (Array.isArray(response.data)) {
        all = response.data;
        break;
      } else {
        const paginated = response.data as {
          results?: ComponentManager[];
          next?: string | null;
        };
        all = [...all, ...(paginated.results || [])];
        const next = paginated.next ?? null;
        if (next) {
          const urlObj = new URL(next);
          url = urlObj.pathname + urlObj.search;
        } else {
          url = null;
        }
      }
    }

    setCache(all);
    return all;
  };

  const checkExistingAssignment = useCallback(async (componentId: string) => {
    setCheckingAssignment(true);
    setExistingAssignment(null);
    setServerDaysToReview(null);
    setFormData(emptyForm);
    try {
      const allData = await fetchAllAssignments();
      const match = allData.find(
        (m) => m.component.id.toString() === componentId,
      );

      if (match) {
        setExistingAssignment(match);

        // Prefer the backend-calculated field if present
        const backendDays = (
          match as ComponentManager & { review_days?: number | null }
        ).review_days;
        setServerDaysToReview(backendDays ?? null);

        setFormData({
          description: match.description || "",
          version: match.version || "",
          lead_developer_id: match.lead_developer?.id.toString() || "",
          backup_developer_id: match.backup_developer?.id.toString() || "",
          user_lead_id:
            (
              match as ComponentManager & {
                user_lead?: { id: number };
              }
            ).user_lead?.id.toString() || "",
          whatsapp_group_url: match.whatsapp_group_url || "",
          figma_url: match.figma_url || "",
          next_audit:
            (match as ComponentManager & { next_audit?: string }).next_audit ||
            "",
        });
      }
    } catch (error) {
      console.error("Failed to check existing assignment:", error);
    } finally {
      setCheckingAssignment(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedComponent) {
      setExistingAssignment(null);
      setServerDaysToReview(null);
      setFormData(emptyForm);
      return;
    }
    checkExistingAssignment(selectedComponent);
  }, [selectedComponent, checkExistingAssignment]);

  const fetchComponents = async () => {
    setLoadingComponents(true);
    try {
      let allComponents: Component[] = [];
      let url: string | null = "/api/endpoint-request/components/";
      while (url) {
        const response: {
          data: { results?: Component[]; next?: string | null } | Component[];
        } = await apiService.get(url);
        if (Array.isArray(response.data)) {
          allComponents = response.data;
          break;
        } else {
          const paginated = response.data as {
            results?: Component[];
            next?: string | null;
          };
          allComponents = [...allComponents, ...(paginated.results || [])];
          const next: string | null = paginated.next ?? null;
          if (next) {
            const urlObj = new URL(next);
            url = urlObj.pathname + urlObj.search;
          } else {
            url = null;
          }
        }
      }
      setComponents(allComponents);
    } catch (error) {
      console.error("Failed to fetch components:", error);
      toast.error("Failed to load components.");
    } finally {
      setLoadingComponents(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      let allUsers: User[] = [];
      let url: string | null = "/api/authentication/users/";
      while (url) {
        const response: {
          data: { results?: User[]; next?: string | null } | User[];
        } = await apiService.get(url);
        if (Array.isArray(response.data)) {
          allUsers = response.data;
          break;
        } else {
          const paginated = response.data as {
            results?: User[];
            next?: string | null;
          };
          allUsers = [...allUsers, ...(paginated.results || [])];
          const next: string | null = paginated.next ?? null;
          if (next) {
            const urlObj: URL = new URL(next);
            url = urlObj.pathname + urlObj.search;
          } else {
            url = null;
          }
        }
      }
      setUsers(allUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserLabel = (user: User) => {
    const fullName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(" ");
    return fullName || user.username;
  };

  const fetchDevUsers = async () => {
    setLoadingUsers(true);
    try {
      let allDevUsers: User[] = [];
      let url: string | null = "/api/authentication/users/?role=dev";
      while (url) {
        const response: {
          data: { results?: User[]; next?: string | null } | User[];
        } = await apiService.get(url);
        if (Array.isArray(response.data)) {
          allDevUsers = response.data;
          break;
        } else {
          const paginated = response.data as {
            results?: User[];
            next?: string | null;
          };
          allDevUsers = [...allDevUsers, ...(paginated.results || [])];
          const next: string | null = paginated.next ?? null;
          if (next) {
            const urlObj = new URL(next);
            url = urlObj.pathname + urlObj.search;
          } else {
            url = null;
          }
        }
      }
      setDevUsers(allDevUsers);
    } catch (error) {
      console.error("Failed to fetch dev users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "next_audit") {
      // User is editing the date — clear server value so live preview kicks in
      setServerDaysToReview(null);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Priority: server value > client-side calculation
  const displayedDays: number | null =
    serverDaysToReview !== null
      ? serverDaysToReview
      : calcDaysToReview(formData.next_audit);
  const chipProps = daysChipProps(displayedDays);

  // ─── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedComponent || !formData.lead_developer_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    const payload = {
      component_id: Number(selectedComponent),
      description: formData.description,
      version: formData.version,
      lead_developer_id: Number(formData.lead_developer_id),
      whatsapp_group_url: formData.whatsapp_group_url || null,
      ...(formData.backup_developer_id && {
        backup_developer_id: Number(formData.backup_developer_id),
      }),
      ...(formData.figma_url && { figma_url: formData.figma_url }),
      ...(formData.user_lead_id && {
        user_lead_id: Number(formData.user_lead_id),
      }),
      next_audit: formData.next_audit || null,
    };

    setSubmitting(true);
    try {
      if (existingAssignment) {
        await apiService.patch(
          `/api/configurations/components-managers/${existingAssignment.id}/`,
          payload,
        );
        toast.success("Assignment updated successfully!");
      } else {
        await apiService.post(
          "/api/configurations/components-managers/",
          payload,
        );
        toast.success("Component assigned successfully!");
      }

      invalidateCache();
      notifyAssignmentsUpdated();
      setOpenModal(true);
      setSelectedComponent("");
      setFormData(emptyForm);
      setExistingAssignment(null);
      setServerDaysToReview(null);
    } catch (error) {
      console.error("Failed to save assignment:", error);
      toast.error("Failed to save assignment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingAssignment) return;
    setDeleting(true);
    try {
      await apiService.delete(
        `/api/configurations/components-managers/${existingAssignment.id}/`,
      );
      invalidateCache();
      notifyAssignmentsUpdated();
      toast.success("Assignment removed successfully!");
      setOpenDeleteDialog(false);
      setSelectedComponent("");
      setFormData(emptyForm);
      setExistingAssignment(null);
      setServerDaysToReview(null);
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      toast.error("Failed to remove assignment. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveNewComponent = async () => {
    if (!newComponentData.name.trim()) {
      toast.error("Component name is required");
      return;
    }
    setSavingNewComponent(true);
    try {
      const response = await apiService.post<Component>(
        "/api/endpoint-request/components/",
        {
          name: newComponentData.name.trim(),
          description: newComponentData.description.trim() || "",
        },
      );
      const saved = response.data;
      toast.success("Component added successfully!");
      setComponents((prev) => [...prev, saved]);
      setSelectedComponent(saved.id.toString());
      setOpenAddComponentDialog(false);
      setNewComponentData({ name: "", description: "" });
    } catch (error) {
      console.error("Failed to save new component:", error);
      toast.error("Failed to add component. Please try again.");
    } finally {
      setSavingNewComponent(false);
    }
  };

  const selectedComponentName =
    components.find((c) => c.id.toString() === selectedComponent)?.name || "";

  // ─── Autocomplete factory ──────────────────────────────────────────────────

  const buildAutocomplete = (
    fieldKey: "lead_developer_id" | "backup_developer_id" | "user_lead_id",
    label: string,
    userList: User[], // NEW
    required = false,
  ) => (
    <Autocomplete
      options={userList}
      value={
        userList.find((u) => u.id.toString() === formData[fieldKey]) || null
      }
      fullWidth
      disabled={!selectedComponent || loadingUsers || checkingAssignment}
      getOptionLabel={(u) => getUserLabel(u)}
      filterOptions={(options, { inputValue }) =>
        inputValue.trim() === ""
          ? []
          : options.filter((u) =>
              getUserLabel(u).toLowerCase().includes(inputValue.toLowerCase()),
            )
      }
      noOptionsText="Type to search for a user"
      onChange={(_, newValue) =>
        setFormData((prev) => ({
          ...prev,
          [fieldKey]: newValue ? newValue.id.toString() : "",
        }))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          placeholder="Type to search…"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingUsers ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Assign Component Lead
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <SectionHeader
          icon={<InfoOutlined fontSize="small" />}
          label="Component"
        />

        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel>Select Component *</InputLabel>
          <Select
            value={selectedComponent}
            label="Select Component *"
            onChange={(e) => setSelectedComponent(e.target.value)}
            disabled={loadingComponents}
            endAdornment={
              loadingComponents ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : null
            }
          >
            {components.map((c) => (
              <MenuItem key={c.id} value={c.id.toString()}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canAdd && (
          <Button
            startIcon={<AddCircleOutlineOutlined />}
            onClick={() => {
              setOpenAddComponentDialog(true);
              setNewComponentData({ name: "", description: "" });
            }}
            size="small"
            sx={{ mb: 1, textTransform: "none" }}
          >
            Add Component
          </Button>
        )}

        {checkingAssignment && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Checking existing assignment…
            </Typography>
          </Box>
        )}

        {!checkingAssignment && existingAssignment && (
          <Alert severity="info" sx={{ mb: 1 }}>
            This component already has an assignment. You can update it below or
            remove it.
          </Alert>
        )}

        {/* ── Details ─────────────────────────────────────────── */}
        <SectionHeader
          icon={<InfoOutlined fontSize="small" />}
          label="Details"
        />

        <CustomTextField
          fullWidth
          margin="dense"
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          disabled={!selectedComponent || checkingAssignment}
          multiline
          rows={3}
        />

        {/* Version + Next Audit Date — side-by-side */}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              margin="dense"
              label="Version"
              name="version"
              value={formData.version}
              onChange={handleChange}
              disabled={!selectedComponent || checkingAssignment}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              margin="dense"
              label="Next Audit Date"
              name="next_audit"
              type="date"
              value={formData.next_audit}
              onChange={handleChange}
              disabled={!selectedComponent || checkingAssignment}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <EventNote
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Days-to-review — shown whenever a component is selected */}
        {selectedComponent && !checkingAssignment && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mt: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: 1,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <AccessTime fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", minWidth: 110 }}
            >
              Days to review:
            </Typography>
            <Chip
              label={chipProps.label}
              color={chipProps.color}
              size="small"
            />
            {serverDaysToReview !== null && (
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", ml: 0.5 }}
              >
                (server value)
              </Typography>
            )}
          </Box>
        )}

        {/* ── Links ───────────────────────────────────────────── */}
        <SectionHeader icon={<LinkIcon fontSize="small" />} label="Links" />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              margin="dense"
              label="WhatsApp Group URL"
              name="whatsapp_group_url"
              value={formData.whatsapp_group_url}
              onChange={handleChange}
              disabled={!selectedComponent || checkingAssignment}
              placeholder="https://chat.whatsapp.com/…"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              margin="dense"
              label="Figma URL"
              name="figma_url"
              value={formData.figma_url}
              onChange={handleChange}
              disabled={!selectedComponent || checkingAssignment}
              placeholder="https://www.figma.com/…"
            />
          </Grid>
        </Grid>

        {/* ── Team ────────────────────────────────────────────── */}
        <SectionHeader icon={<Group fontSize="small" />} label="Team" />

        {/* Lead Developer — full width (most important) */}
        <Box sx={{ mb: 2 }}>
          {buildAutocomplete(
            "lead_developer_id",
            "Lead Developer",
            devUsers,
            true,
          )}
        </Box>

        {/* Backup Developer + User Lead — side-by-side */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {buildAutocomplete(
              "backup_developer_id",
              "Backup Developer",
              devUsers,
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {buildAutocomplete("user_lead_id", "User Lead (Feedback)", users)}
          </Grid>
        </Grid>

        {/* ── Actions ─────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          {(existingAssignment ? canEdit : canAdd) && (
            <Button
              type="submit"
              variant="contained"
              disabled={!selectedComponent || submitting || checkingAssignment}
            >
              {submitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  {existingAssignment ? "Updating…" : "Assigning…"}
                </>
              ) : existingAssignment ? (
                "Update Assignment"
              ) : (
                "Assign"
              )}
            </Button>
          )}

          {existingAssignment && canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => setOpenDeleteDialog(true)}
              disabled={submitting || checkingAssignment}
            >
              Remove Assignment
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Success modal ───────────────────────────────────────── */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 2 }}>
            Component assignment saved successfully!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation ──────────────────────────────────── */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Assignment</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 2 }}>
            Are you sure you want to remove the assignment for{" "}
            <strong>{selectedComponentName}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Removing…
              </>
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Component dialog ──────────────────────────────────── */}
      <Dialog
        open={openAddComponentDialog}
        onClose={() => setOpenAddComponentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Component</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Component Name *"
            fullWidth
            value={newComponentData.name}
            onChange={(e) =>
              setNewComponentData((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Component Description"
            fullWidth
            multiline
            rows={4}
            value={newComponentData.description}
            onChange={(e) =>
              setNewComponentData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder="Describe what this component is used for…"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenAddComponentDialog(false)}
            disabled={savingNewComponent}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewComponent}
            variant="contained"
            disabled={savingNewComponent || !newComponentData.name.trim()}
          >
            {savingNewComponent ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UpdateFooter;
